import React, { useRef } from "react";
import { Box, Typography, Chip, Tooltip, Stack } from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { BASE_DICT_SOUND_US } from "@/constants";
import { MappingIPA } from "@/constants/mappingIPA";
import { getUrlServer } from "@/helpers";

export interface IPhoneme {
  phone: string;
  qualityScore: number;
  soundMostLike?: string;
  extent?: [number, number];
}

interface PhonemeBreakdownProps {
  word: string;
  phonemes: IPhoneme[];
  userAudioUrl?: any; // User recording (string URL, Blob/File object, or payload object with audioFile)
  extent?: [number, number]; // Start/end time of word in user recording
  uid?: string;
}

const PhonemeBreakdown: React.FC<PhonemeBreakdownProps> = ({ word, phonemes, userAudioUrl, extent, uid }) => {
  const userAudioRef = useRef<HTMLAudioElement | null>(null);
  const timeoutRef = useRef<any>(null);

  const handlePlayStandard = () => {
    if (uid) {
      const soundUrl = `${BASE_DICT_SOUND_US}${uid}.mp3`;
      const audio = new Audio(soundUrl);
      audio.play().catch((e) => console.error("Error playing dictionary audio:", e));
    } else if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(word);
      utterance.lang = "en-US";
      const voices = window.speechSynthesis.getVoices();
      const savedVoiceURI = typeof window !== "undefined" ? localStorage.getItem("fong_selected_tts_voice") : null;
      const enVoice =
        (savedVoiceURI && voices.find((v) => v.voiceURI === savedVoiceURI)) ||
        voices.find(
          (v) => v.lang.startsWith("en") && (v.name.toLowerCase().includes("google") || v.localService === false)
        ) ||
        voices.find((v) => v.lang.startsWith("en-US"));
      if (enVoice) {
        utterance.voice = enVoice;
        utterance.lang = enVoice.lang;
      }
      window.speechSynthesis.speak(utterance);
    }
  };

  const handlePlayUser = () => {
    if (!userAudioUrl) return;

    const finalAudioUrl = getUrlServer(userAudioUrl);
    if (!finalAudioUrl) return;

    if (userAudioRef.current) {
      userAudioRef.current.pause();
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const audio = new Audio(finalAudioUrl);
    userAudioRef.current = audio;

    if (extent && extent.length === 2) {
      const startSeconds = extent[0] / 100;
      const endSeconds = extent[1] / 100;
      const startTime = Math.max(0, startSeconds - 0.05);
      const endTime = endSeconds + 0.05;
      const duration = Math.max(150, (endTime - startTime) * 1000);

      const playSegment = () => {
        try {
          audio.currentTime = startTime;
        } catch (e) {
          console.warn("Could not set currentTime:", e);
        }
        timeoutRef.current = setTimeout(() => {
          audio.pause();
        }, duration);
      };

      // Execute audio.play() synchronously inside user click gesture stack for browser Autoplay policy compliance
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (audio.readyState >= 1) {
              playSegment();
            } else {
              audio.onloadedmetadata = playSegment;
            }
          })
          .catch((e) => console.error("Error playing user audio:", e));
      }
    } else {
      audio.play().catch((e) => console.error("Error playing user audio:", e));
    }
  };

  return (
    <Box sx={{ mt: 1, p: 1.5, bgcolor: "rgba(0,0,0,0.02)", borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", mb: 1.5, gap: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
          Chi tiết âm:
        </Typography>

        <Stack direction="row" spacing={1}>
          <Tooltip title={!userAudioUrl ? "Bạn cần ghi âm mới nghe được giọng mình" : "Nghe lại đoạn bạn nói từ này"}>
            <span>
              <Chip
                icon={<RecordVoiceOverIcon sx={{ fontSize: "14px !important" }} />}
                label="Giọng bạn"
                size="small"
                onClick={handlePlayUser}
                disabled={!userAudioUrl}
                sx={{
                  height: 24,
                  fontSize: "11px",
                  cursor: !userAudioUrl ? "default" : "pointer",
                  bgcolor: "#fff",
                  opacity: !userAudioUrl ? 0.5 : 1,
                }}
                variant="outlined"
                color="info"
              />
            </span>
          </Tooltip>

          <Chip
            icon={<VolumeUpIcon sx={{ fontSize: "14px !important" }} />}
            label="Giọng chuẩn"
            size="small"
            onClick={handlePlayStandard}
            sx={{ height: 24, fontSize: "11px", cursor: "pointer", bgcolor: "#fff" }}
            variant="outlined"
            color="success"
          />
        </Stack>
      </Box>

      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {phonemes.map((phoneme, idx) => {
          const isGood = phoneme.qualityScore >= 60;
          return (
            <Tooltip
              key={idx}
              title={
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="caption" display="block">
                    Score: {phoneme.qualityScore}
                  </Typography>
                  {phoneme.soundMostLike && !isGood && (
                    <Typography variant="caption" display="block" color="error.light">
                      Nghe giống: /
                      {MappingIPA[phoneme.soundMostLike.toLowerCase() as keyof typeof MappingIPA] ||
                        phoneme.soundMostLike}
                      /
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <Chip
                label={MappingIPA[phoneme.phone.toLowerCase() as keyof typeof MappingIPA] || phoneme.phone}
                size="small"
                sx={{
                  height: 24,
                  minWidth: 24,
                  backgroundColor: isGood ? "rgba(46, 125, 50, 0.1)" : "rgba(211, 47, 47, 0.1)",
                  borderColor: isGood ? "#66bb6a" : "#ef5350",
                  border: "1px solid",
                  color: isGood ? "#2e7d32" : "#d32f2f",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: isGood ? "rgba(46, 125, 50, 0.2)" : "rgba(211, 47, 47, 0.2)",
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Show mispronounced phonemes summary */}
      {phonemes.some((p) => p.soundMostLike && p.qualityScore < 60) && (
        <Box sx={{ mt: 1 }}>
          {phonemes
            .filter((p) => p.soundMostLike && p.qualityScore < 60)
            .map((p, i) => (
              <Typography key={i} variant="caption" display="block" sx={{ color: "#d32f2f", fontStyle: "italic" }}>
                ⚠️ Âm <b>/{MappingIPA[p.phone as keyof typeof MappingIPA] || p.phone}/</b> nghe giống{" "}
                <b>/{MappingIPA[p.soundMostLike as keyof typeof MappingIPA] || p.soundMostLike}/</b>
              </Typography>
            ))}
        </Box>
      )}
    </Box>
  );
};

export default PhonemeBreakdown;
